import {
  Cartesian3,
  Math as CesiumMath,
  Color,
  Rectangle,
  ScreenSpaceEventHandler,
  defined,
  ScreenSpaceEventType,
  PolylineDashMaterialProperty,
  Cartographic,
  CallbackProperty,
  JulianDate,
  CustomDataSource,
  PolygonHierarchy,
  Entity,
  ColorMaterialProperty,
  ConstantPositionProperty,
  PropertyBag,
  EllipsoidGeodesic,
  HeightReference,
  KeyboardEventModifier,
} from "cesium";

import { createUniqueId, getUsernameAndUserIdFormat, showToast } from "./Utility";
import Type from "./Types";
import { disableCameraControls, restoreCameraControls } from "./MapUtility";

export const deleteDrawingForSelect = (viewer) => {
  const existingPolygon = viewer.entities.getById("drawn-for-select");
  if (existingPolygon) {
    viewer.entities.remove(existingPolygon);
  }

  const existingPolygon2 = viewer.entities.getById("drawnborder-for-select");
  if (existingPolygon2) {
    viewer.entities.remove(existingPolygon2);
  }

  const entities = viewer.entities.values;
  for (let i = entities.length - 1; i >= 0; i--) {
    const entity = entities[i];
    if (entity.id.startsWith("polygon-point-")) {
      viewer.entities.remove(entity);
    }
  }
};

export const resetHIBHighlights = (viewer) => {
  if (!viewer) return;

  // 1) Remove overlay datasource (yellow overlays for f_p / p_f / p_p)
  const name = "__HIB_OVERLAYS__";
  const list = viewer.dataSources._dataSources || [];
  const idx = list.findIndex((ds) => ds.name === name || ds._name === name);
  if (idx >= 0) viewer.dataSources.remove(list[idx], true);

  // 2) Helper to restore a graphics object's backed-up props
  const restoreProp = (obj, key, backupKey = `__hib_orig_${key}`) => {
    if (obj && obj[backupKey] !== undefined) {
      obj[key] = obj[backupKey];
      delete obj[backupKey];
    }
  };

  function restoreEntity(entity) {
    if (!entity) return;

    // Polyline / Path
    if (entity.polyline) {
      restoreProp(entity.polyline, "material");
      restoreProp(entity.polyline, "width");
    }
    if (entity.path) {
      restoreProp(entity.path, "material");
      restoreProp(entity.path, "width");
    }

    // Polygon-like
    if (entity.polygon) {
      restoreProp(entity.polygon, "material");
      restoreProp(entity.polygon, "outline");
      restoreProp(entity.polygon, "outlineColor");
    }
    if (entity.corridor) {
      restoreProp(entity.corridor, "material");
    }
    if (entity.rectangle) {
      restoreProp(entity.rectangle, "material");
      restoreProp(entity.rectangle, "outline");
      restoreProp(entity.rectangle, "outlineColor");
    }
    if (entity.ellipse) {
      restoreProp(entity.ellipse, "material");
      restoreProp(entity.ellipse, "outline");
      restoreProp(entity.ellipse, "outlineColor");
    }
    if (entity.ellipsoid) {
      restoreProp(entity.ellipsoid, "material");
      restoreProp(entity.ellipsoid, "outline");
      restoreProp(entity.ellipsoid, "outlineColor");
    }

    // Point / Billboard / Label
    if (entity.point) {
      restoreProp(entity.point, "color");
      restoreProp(entity.point, "pixelSize");
    }
    if (entity.billboard) {
      restoreProp(entity.billboard, "color");
    }
    if (entity.label) {
      restoreProp(entity.label, "fillColor");
      // restoreProp(entity.label, "outlineColor");
    }

    // Remove temporary point added by highlighter
    if (entity.__hib_addedPoint && entity.point) {
      entity.point = undefined;
      delete entity.__hib_addedPoint;
    }
  }

  // 3) Walk all data sources + viewer.entities
  const dsList2 = viewer.dataSources._dataSources || [];
  for (let d = 0; d < dsList2.length; d++) {
    const ds = dsList2[d];
    const values = ds?.entities?.values || [];
    for (let i = 0; i < values.length; i++) {
      restoreEntity(values[i]);
    }
  }
  const mainValues = viewer.entities?.values || [];
  for (let i = 0; i < mainValues.length; i++) {
    restoreEntity(mainValues[i]);
  }
};

// installEmptyEntityClick.js
// Run at startup. Calls callback only when click hits no Entity.
export const installEmptyEntityClick = (viewer, callback, options = {}) => {
  const {
    button = "LEFT", // LEFT | MIDDLE | RIGHT
    useDrillPick = false, // set true if using clustered layers
    includeGround = true, // provide cartesian/cartographic of ground
  } = options;

  const evtMap = {
    LEFT: ScreenSpaceEventType.LEFT_CLICK,
    MIDDLE: ScreenSpaceEventType.MIDDLE_CLICK,
    RIGHT: ScreenSpaceEventType.RIGHT_CLICK,
  };

  const handler = new ScreenSpaceEventHandler(viewer.canvas);

  // Check if picked object is a Cesium Entity (in viewer or any DataSource)
  const isEntityHit = (picked) => {
    if (!picked) return false;
    const items = Array.isArray(picked) ? picked : [picked];
    for (const p of items) {
      const id = p && p.id;
      if (!id) continue;
      if (viewer.entities?.contains?.(id)) return true;
      const dsLen = viewer.dataSources?.length ?? 0;
      for (let i = 0; i < dsLen; i++) {
        const ds = viewer.dataSources.get(i);
        if (ds?.entities?.contains?.(id)) return true;
      }
    }
    return false;
  };

  // Window -> Cartesian (depth first, ellipsoid fallback)
  const pickCartesian = (winPos) => {
    if (!winPos || !viewer) return undefined;

    const { scene } = viewer;
    let c;

    if (scene?.pickPositionSupported) {
      try {
        c = scene.pickPosition(winPos);
      } catch {
        c = undefined;
      }
    }

    if (!defined(c)) {
      c = scene.camera.pickEllipsoid(winPos, scene.globe.ellipsoid);
    }

    return defined(c) ? c : undefined;
  };

  handler.setInputAction((click) => {
    if (!click?.position) return;

    // Pick objects under cursor
    const scene = viewer.scene;
    const picked = useDrillPick ? scene.drillPick(click.position) : scene.pick(click.position);

    // If any Entity was hit, do nothing
    if (isEntityHit(picked)) return;

    // Prepare payload
    let cartesian, cartographic;
    if (includeGround) {
      cartesian = pickCartesian(click.position);
      if (cartesian) cartographic = Cartographic.fromCartesian(cartesian);
    }

    // Fire callback
    callback({
      windowPosition: click.position, // Cesium.Cartesian2
      cartesian, // Cesium.Cartesian3 | undefined
      cartographic, // Cesium.Cartographic | undefined
    });
  }, evtMap[button]);

  // Return disposer
  return {
    destroy: () => {
      try {
        handler.destroy();
      } catch {}
    },
  };
};

export const deleteSelectionFromViewer = (viewer, selection, mapObjectsRef, setMapObjects, options = {}) => {
  const removeEmpty = options.removeEmpty !== false; // default true
  const doLog = !!options.log;

  if (!viewer || !selection || !selection.results) {
    return { removedEntities: 0, modifiedEntities: 0, removedVertices: 0, undo: [] };
  }

  const undo = []; // collect undo operations

  function findEntityById(id) {
    const dsList = viewer.dataSources && viewer.dataSources._dataSources ? viewer.dataSources._dataSources : [];
    for (let i = 0; i < dsList.length; i++) {
      const ds = dsList[i];
      const coll = ds?.entities;
      if (!coll) continue;
      const ent = coll.getById ? coll.getById(id) : null;
      if (ent) return { entity: ent, owner: coll };
      const vals = coll.values || [];
      for (let j = 0; j < vals.length; j++) {
        if (vals[j].id === id) return { entity: vals[j], owner: coll };
      }
    }
    const main = viewer.entities;
    if (main) {
      const ent = main.getById ? main.getById(id) : null;
      if (ent) return { entity: ent, owner: main };
      const vals = main.values || [];
      for (let j = 0; j < vals.length; j++) {
        if (vals[j].id === id) return { entity: vals[j], owner: main };
      }
    }
    return { entity: null, owner: null };
  }

  function readGeometry(entity) {
    if (entity.polyline?.positions) {
      const pos = entity.polyline.positions.getValue ? entity.polyline.positions.getValue() : entity.polyline.positions;
      if (Array.isArray(pos) && pos.length) return { type: "polyline", positions: pos };
    }
    if (entity.polygon?.hierarchy) {
      const h = entity.polygon.hierarchy.getValue ? entity.polygon.hierarchy.getValue() : entity.polygon.hierarchy;
      if (h && Array.isArray(h.positions) && h.positions.length) {
        return { type: "polygon", positions: h.positions };
      }
      if (Array.isArray(h) && h.length) {
        return { type: "polygon", positions: h };
      }
    }
    if (entity.position?.getValue) {
      const p = entity.position.getValue();
      if (p) return { type: "point", positions: [p] };
    }
    return null;
  }

  function writeGeometryOrRemove(owner, entity, type, newPositions) {
    if (type === "point") {
      if (!newPositions || newPositions.length === 0) {
        owner.remove(entity);
        return { removed: true, modified: false };
      }
      return { removed: false, modified: false };
    }

    if (type === "polyline") {
      if (!newPositions || newPositions.length < 2) {
        if (removeEmpty) {
          owner.remove(entity);
          return { removed: true, modified: false };
        }
        return { removed: false, modified: false };
      }
      entity.polyline.positions = newPositions;
      return { removed: false, modified: true };
    }

    if (type === "polygon") {
      if (!newPositions || newPositions.length < 3) {
        if (removeEmpty) {
          owner.remove(entity);
          return { removed: true, modified: false };
        }
        return { removed: false, modified: false };
      }
      if (typeof Cesium !== "undefined" && PolygonHierarchy) {
        entity.polygon.hierarchy = new PolygonHierarchy(newPositions);
      } else if (typeof PolygonHierarchy !== "undefined") {
        entity.polygon.hierarchy = new PolygonHierarchy(newPositions);
      } else {
        entity.polygon.hierarchy = { positions: newPositions };
      }
      return { removed: false, modified: true };
    }

    return { removed: false, modified: false };
  }

  function filterByRemovingRanges(arr, ranges) {
    if (!ranges.length) return arr.slice();

    const merged = ranges
      .slice()
      .sort((a, b) => a[0] - b[0])
      .reduce((acc, r) => {
        if (!acc.length) return [r.slice()];
        const last = acc[acc.length - 1];
        if (r[0] <= last[1] + 1) {
          last[1] = Math.max(last[1], r[1]);
        } else {
          acc.push(r.slice());
        }
        return acc;
      }, []);

    const out = [];
    let ri = 0;
    for (let i = 0; i < arr.length; i++) {
      const range = merged[ri];
      if (!range || i < range[0]) {
        out.push(arr[i]);
      } else if (i > range[1]) {
        ri++;
        i--;
      } else {
        // skip
      }
    }
    return out;
  }

  let removedEntities = 0;
  let modifiedEntities = 0;
  let removedVertices = 0;

  const st = selection.selectionType;
  const results = selection.results || {};

  let temp = [...mapObjectsRef.current];
  for (const obj of temp) {
    for (const key in selection.results[st]) {
      const { entity, owner } = findEntityById(selection.results[st][key].id);
      if (obj.dataSource?.entities?.contains(entity)) {
        obj.is_edited = true;
        break;
      }
    }
  }
  setMapObjects(temp);

  if (st === "f_f" && Array.isArray(results.f_f)) {
    // remove whole entities
    for (const ref of results.f_f) {
      const { entity, owner } = findEntityById(ref.id);
      if (!entity || !owner) continue;

      // undo: add entity back to owner
      undo.push({
        kind: "remove_entity",
        selectionType: "f_f",
        id: ref.id,
        entity,
        owner,
      });

      owner.remove(entity);
      removedEntities++;
      if (doLog) console.log("[deleteSelection] Removed entity (f_f):", ref.id);
    }
  } else if (st === "f_p" && Array.isArray(results.f_p)) {
    for (const ref of results.f_p) {
      const { entity, owner } = findEntityById(ref.id);
      if (!entity || !owner) continue;

      const geom = readGeometry(entity);
      if (!geom || (geom.type !== "polyline" && geom.type !== "polygon")) continue;

      const oldPositions = geom.positions.slice();

      if (ref.selectedWhole) {
        undo.push({
          kind: "remove_entity",
          selectionType: "f_p",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
        });

        if (owner.remove) {
          owner.remove(entity);
          removedEntities++;
          removedVertices += oldPositions.length;
          if (doLog) console.log("[deleteSelection] Removed whole entity (f_p selectedWhole):", ref.id);
        }
        continue;
      }

      const ranges = [];
      for (const part of ref.parts || []) {
        const s = typeof part.startIndex === "number" ? part.startIndex : null;
        const e = typeof part.endIndex === "number" ? part.endIndex : null;
        if (s !== null && e !== null) {
          ranges.push([Math.max(0, Math.min(s, e)), Math.min(oldPositions.length - 1, Math.max(s, e))]);
        }
      }
      if (!ranges.length) continue;

      const before = oldPositions.length;
      const kept = filterByRemovingRanges(oldPositions, ranges);
      const after = kept.length;

      const res = writeGeometryOrRemove(owner, entity, geom.type, kept);

      if (res.removed) {
        removedEntities++;
        removedVertices += before;
        undo.push({
          kind: "remove_entity",
          selectionType: "f_p",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
        });
        if (doLog) console.log("[deleteSelection] Removed entity due to empty geometry (f_p):", ref.id);
      } else if (res.modified) {
        modifiedEntities++;
        removedVertices += before - after;
        undo.push({
          kind: "modify_geometry",
          selectionType: "f_p",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
          positionsAfter: kept,
        });
        if (doLog) console.log("[deleteSelection] Trimmed in-bounds parts (f_p):", ref.id, "(-" + (before - after) + " verts)");
      }
    }
  } else if (st === "p_f" && Array.isArray(results.p_f)) {
    for (const ref of results.p_f) {
      const { entity, owner } = findEntityById(ref.id);
      if (!entity || !owner) continue;

      const geom = readGeometry(entity);
      if (!geom) continue;

      const oldPositions = geom.positions.slice();

      if (geom.type === "point") {
        undo.push({
          kind: "remove_entity",
          selectionType: "p_f",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
        });

        owner.remove(entity);
        removedEntities++;
        removedVertices += 1;
        if (doLog) console.log("[deleteSelection] Removed point (p_f):", ref.id);
        continue;
      }

      const indices = new Set();
      for (const pt of ref.points || []) {
        if (typeof pt.index === "number") indices.add(pt.index);
      }
      if (!indices.size) continue;

      const before = oldPositions.length;
      const kept = oldPositions.filter((_, i) => !indices.has(i));
      const after = kept.length;

      const res = writeGeometryOrRemove(owner, entity, geom.type, kept);

      if (res.removed) {
        removedEntities++;
        removedVertices += before;
        undo.push({
          kind: "remove_entity",
          selectionType: "p_f",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
        });
        if (doLog) console.log("[deleteSelection] Removed entity due to empty geometry (p_f):", ref.id);
      } else if (res.modified) {
        modifiedEntities++;
        removedVertices += before - after;
        undo.push({
          kind: "modify_geometry",
          selectionType: "p_f",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
          positionsAfter: kept,
        });
        if (doLog) console.log("[deleteSelection] Removed " + (before - after) + " vertices (p_f):", ref.id);
      }
    }
  } else if (st === "p_p" && Array.isArray(results.p_p)) {
    for (const ref of results.p_p) {
      const { entity, owner } = findEntityById(ref.id);
      if (!entity || !owner) continue;

      const geom = readGeometry(entity);
      if (!geom) continue;

      const oldPositions = geom.positions.slice();

      if (geom.type === "point") {
        undo.push({
          kind: "remove_entity",
          selectionType: "p_p",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
        });

        owner.remove(entity);
        removedEntities++;
        removedVertices += 1;
        if (doLog) console.log("[deleteSelection] Removed point (p_p):", ref.id);
        continue;
      }

      const toRemove = new Set();
      const sIdx = ref.endpoints?.start?.index;
      const eIdx = ref.endpoints?.end?.index;
      if (typeof sIdx === "number") toRemove.add(sIdx);
      if (typeof eIdx === "number") toRemove.add(eIdx);

      if (!toRemove.size) continue;

      const before = oldPositions.length;
      const kept = oldPositions.filter((_, i) => !toRemove.has(i));
      const after = kept.length;

      const res = writeGeometryOrRemove(owner, entity, geom.type, kept);

      if (res.removed) {
        removedEntities++;
        removedVertices += before;
        undo.push({
          kind: "remove_entity",
          selectionType: "p_p",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
        });
        if (doLog) console.log("[deleteSelection] Removed entity due to empty geometry (p_p):", ref.id);
      } else if (res.modified) {
        modifiedEntities++;
        removedVertices += before - after;
        undo.push({
          kind: "modify_geometry",
          selectionType: "p_p",
          id: ref.id,
          entity,
          owner,
          type: geom.type,
          positionsBefore: oldPositions,
          positionsAfter: kept,
        });
        if (doLog) console.log("[deleteSelection] Removed endpoints (p_p):", ref.id, "(-" + (before - after) + " verts)");
      }
    }
  } else {
    if (doLog) console.warn('[deleteSelection] Nothing to delete for selectionType="' + st + '".');
  }

  return { removedEntities, modifiedEntities, removedVertices, undo };
};

// GlobalToolsUtility.js
export const applyPPSelectionToCoordinate = (viewer, selection, target, mapObjectsRef, setMapObjects, options = {}, userData) => {
  const enforceClosure = options.enforceClosure !== false; // default true
  const doLog = !!options.log;
  const maxDistanceMeters = typeof options.maxDistanceMeters === "number" ? options.maxDistanceMeters : 5; // default 5m
  const modifier = getUsernameAndUserIdFormat(userData);

  if (!viewer || !selection || selection.selectionType !== "p_p" || !selection.results || !Array.isArray(selection.results.p_p)) {
    return { modifiedEntities: 0, updatedVertices: 0, skipped: 0, tooFar: 0, undo: [] };
  }

  // --- Helpers --------------------------------------------------------------

  function getTargetLon(t) {
    if (typeof t.lon === "number") return t.lon;
    if (typeof t.lng === "number") return t.lng;
    throw new Error("Target must include lon or lng.");
  }

  const scratchCartoA = new Cartographic();
  const scratchCartoB = new Cartographic();
  const scratchGeodesic = new EllipsoidGeodesic();

  function toLLH(cart) {
    const carto = Cartographic.fromCartesian(cart);
    return {
      lat: CesiumMath.toDegrees(carto.latitude),
      lon: CesiumMath.toDegrees(carto.longitude),
      height: carto.height,
    };
  }

  function horizontalDistanceXY(a, b) {
    // Surface distance only, ignore height
    Cartographic.fromCartesian(a, undefined, scratchCartoA);
    Cartographic.fromCartesian(b, undefined, scratchCartoB);
    scratchCartoA.height = 0;
    scratchCartoB.height = 0;
    scratchGeodesic.setEndPoints(scratchCartoA, scratchCartoB);
    return scratchGeodesic.surfaceDistance;
  }

  function isClosed(positions) {
    if (!positions || positions.length < 2) return false;
    if (typeof Cartesian3 !== "undefined" && Cartesian3.equalsEpsilon) {
      return Cartesian3.equalsEpsilon(positions[0], positions[positions.length - 1], CesiumMath.EPSILON7);
    }
    const a = toLLH(positions[0]);
    const b = toLLH(positions[positions.length - 1]);
    return Math.abs(a.lat - b.lat) < 1e-7 && Math.abs(a.lon - b.lon) < 1e-7;
  }

  function readGeometry(entity) {
    if (entity.polyline?.positions) {
      const pos = entity.polyline.positions.getValue ? entity.polyline.positions.getValue() : entity.polyline.positions;
      if (Array.isArray(pos) && pos.length) return { type: "polyline", positions: pos };
    }
    if (entity.polygon?.hierarchy) {
      const h = entity.polygon.hierarchy.getValue ? entity.polygon.hierarchy.getValue() : entity.polygon.hierarchy;
      if (h && Array.isArray(h.positions) && h.positions.length) return { type: "polygon", positions: h.positions };
      if (Array.isArray(h) && h.length) return { type: "polygon", positions: h };
    }
    if (entity.position?.getValue) {
      const p = entity.position.getValue();
      if (p) return { type: "point", positions: [p] };
    }
    return null;
  }

  function writeGeometry(entity, type, newPositions) {
    if (type === "point") return;
    if (type === "polyline") {
      entity.polyline.positions = newPositions;
      return;
    }
    if (type === "polygon") {
      if (typeof PolygonHierarchy !== "undefined") {
        entity.polygon.hierarchy = new PolygonHierarchy(newPositions);
      } else {
        entity.polygon.hierarchy = { positions: newPositions };
      }
    }
  }

  // --- Fast entity lookup (cache + per-collection index) --------------------

  const entityCache = new Map(); // id -> { entity, owner }
  const valuesIndexCache = new WeakMap(); // EntityCollection -> Map(id->entity)

  function getValuesIndex(coll) {
    let idx = valuesIndexCache.get(coll);
    if (idx) return idx;
    idx = new Map();
    const vals = coll?.values || [];
    for (let i = 0; i < vals.length; i++) idx.set(vals[i].id, vals[i]);
    valuesIndexCache.set(coll, idx);
    return idx;
  }

  function findEntityById(id) {
    if (entityCache.has(id)) return entityCache.get(id);

    const dsList = viewer.dataSources && viewer.dataSources._dataSources ? viewer.dataSources._dataSources : [];

    for (let i = 0; i < dsList.length; i++) {
      const coll = dsList[i]?.entities;
      if (!coll) continue;

      const ent = coll.getById ? coll.getById(id) : null;
      if (ent) {
        const out = { entity: ent, owner: coll };
        entityCache.set(id, out);
        return out;
      }

      // Keep original behavior: fallback scan (but indexed once)
      const idx = getValuesIndex(coll);
      const ent2 = idx.get(id) || null;
      if (ent2) {
        const out = { entity: ent2, owner: coll };
        entityCache.set(id, out);
        return out;
      }
    }

    const main = viewer.entities;
    if (main) {
      const ent = main.getById ? main.getById(id) : null;
      if (ent) {
        const out = { entity: ent, owner: main };
        entityCache.set(id, out);
        return out;
      }

      const idx = getValuesIndex(main);
      const ent2 = idx.get(id) || null;
      if (ent2) {
        const out = { entity: ent2, owner: main };
        entityCache.set(id, out);
        return out;
      }
    }

    const out = { entity: null, owner: null };
    entityCache.set(id, out);
    return out;
  }

  // --- Precompute selection entities once -----------------------------------

  const refs = selection.results.p_p;
  const selectedEntities = [];
  for (let i = 0; i < refs.length; i++) {
    const { entity } = findEntityById(refs[i].id);
    if (entity) selectedEntities.push(entity);
  }

  // Mark edited map objects (same behavior, fewer lookups)
  const temp = Array.isArray(mapObjectsRef.current) ? mapObjectsRef.current.slice() : [];
  for (let i = 0; i < temp.length; i++) {
    const obj = temp[i];
    const coll = obj?.dataSource?.entities;
    if (!coll || typeof coll.contains !== "function") continue;

    for (let j = 0; j < selectedEntities.length; j++) {
      if (coll.contains(selectedEntities[j])) {
        obj.is_edited = true;
        break;
      }
    }
  }
  setMapObjects(temp);

  // --- Main -----------------------------------------------------------------

  const lat = Number(target.lat);
  const lon = Number(getTargetLon(target));
  const hasHeight = typeof target.height === "number";

  let modifiedEntities = 0;
  let updatedVertices = 0;
  let skipped = 0;
  let tooFar = 0;

  // Lazy undo cloning: always return full undo like original, but clone only when needed
  const undoMap = new Map(); // entity.id -> { entity, type, positionsBefore, positionsRef }

  function ensureUndoSnapshot(entry) {
    if (entry.positionsBefore) return;
    entry.positionsBefore = entry.positionsRef.map((p) => Cartesian3.clone(p));
  }

  for (let r = 0; r < refs.length; r++) {
    const ref = refs[r];
    const { entity } = findEntityById(ref.id);

    if (!entity) {
      skipped++;
      continue;
    }

    const geom = readGeometry(entity);
    if (!geom) {
      skipped++;
      continue;
    }

    // Keep original behavior: add undo entry even if no change happens later
    let undoEntry = undoMap.get(entity.id);
    if (!undoEntry) {
      undoEntry = {
        entity,
        type: geom.type,
        positionsBefore: null,
        positionsRef: geom.positions,
      };
      undoMap.set(entity.id, undoEntry);
    }

    // Point case
    if (geom.type === "point") {
      const currLLH = toLLH(geom.positions[0]);
      const h = hasHeight ? target.height : currLLH.height;
      const candidate = Cartesian3.fromDegrees(lon, lat, h);

      const dist = horizontalDistanceXY(geom.positions[0], candidate);
      if (dist > maxDistanceMeters) {
        tooFar++;
        skipped++;
        if (doLog) {
          console.warn(`[applyPPSelection] Point ${ref.id} horizontal dist ${dist.toFixed(2)}m > ${maxDistanceMeters}m, snap skipped`);
        }
        continue;
      }

      // Snapshot only if we will mutate (perf)
      ensureUndoSnapshot(undoEntry);

      if (typeof ConstantPositionProperty !== "undefined") {
        entity.position = new ConstantPositionProperty(candidate);
      } else {
        entity.position = candidate;
      }

      if (modifier) {
        const currentProps = entity.properties?.getValue ? entity.properties.getValue() || {} : entity.properties || {};
        entity.properties = new PropertyBag({
          ...currentProps,
          modifier,
        });
      }

      modifiedEntities++;
      updatedVertices += 1;
      if (doLog) {
        console.log(`[applyPPSelection] Moved point ${ref.id} -> (${lat}, ${lon}, ${h})`);
      }
      continue;
    }

    // Polyline / Polygon
    const positions = geom.positions.slice();
    const idxSet = new Set();

    const sIdx = ref.endpoints?.start?.index;
    const eIdx = ref.endpoints?.end?.index;
    if (typeof sIdx === "number") idxSet.add(sIdx);
    if (typeof eIdx === "number") idxSet.add(eIdx);

    if (idxSet.size === 0) {
      skipped++;
      continue;
    }

    const closed = enforceClosure && isClosed(positions);
    const lastIndex = positions.length - 1;

    let entityChanged = false;

    for (const i of idxSet) {
      if (i < 0 || i > lastIndex) continue;

      const origLLH = toLLH(positions[i]);
      const h = hasHeight ? target.height : origLLH.height;
      const candidate = Cartesian3.fromDegrees(lon, lat, h);

      const dist = horizontalDistanceXY(positions[i], candidate);
      if (dist > maxDistanceMeters) {
        tooFar++;
        if (doLog) {
          console.warn(`[applyPPSelection] Vertex ${ref.id}[${i}] horizontal dist ${dist.toFixed(2)}m > ${maxDistanceMeters}m, snap skipped`);
        }
        continue;
      }

      // Snapshot only if we will mutate (perf)
      if (!entityChanged) ensureUndoSnapshot(undoEntry);

      positions[i] = candidate;
      updatedVertices++;
      entityChanged = true;

      if (closed && (i === 0 || i === lastIndex)) {
        const otherIndex = i === 0 ? lastIndex : 0;
        const otherLLH = toLLH(positions[otherIndex]);
        const h2 = hasHeight ? target.height : otherLLH.height;
        positions[otherIndex] = Cartesian3.fromDegrees(lon, lat, h2);
        updatedVertices++;
      }
    }

    if (!entityChanged) continue;

    writeGeometry(entity, geom.type, positions);

    if (modifier) {
      const currentProps = entity.properties?.getValue ? entity.properties.getValue() || {} : entity.properties || {};
      entity.properties = new PropertyBag({
        ...currentProps,
        modifier,
      });
    }

    modifiedEntities++;
    if (doLog) {
      console.log(`[applyPPSelection] Updated ${ref.id} (${geom.type}) endpoints -> (${lat}, ${lon}${hasHeight ? ", " + target.height : ""})`);
    }
  }

  const undo = Array.from(undoMap.values()).map((item) => ({
    entity: item.entity,
    type: item.type,
    positionsBefore: item.positionsBefore ? item.positionsBefore : item.positionsRef.map((p) => Cartesian3.clone(p)),
  }));

  return { modifiedEntities, updatedVertices, skipped, tooFar, undo };
};

export const highlightEntitiesInBounds = (viewer, rawBounds, selectionType = "f_f", options = {}) => {
  // Quick guards
  if (!viewer || !viewer.dataSources || !rawBounds) {
    return {
      found: false,
      selectionType,
      boundsKind: Array.isArray(rawBounds) ? "polygon" : "rectangle",
      stats: { entitiesChecked: 0, entitiesMatched: 0 },
      results: {},
    };
  }

  // Options and color fallbacks (must exist)
  const highlightColor = options.highlightColor || (typeof Color !== "undefined" ? Color.ORANGE : undefined);
  const purpleColor = options.purpleColor || (typeof Color !== "undefined" ? Color.PURPLE : undefined);
  if (!highlightColor) throw new Error("Color.YELLOW is not available in scope.");
  if (!purpleColor) throw new Error("Color.PURPLE is not available in scope.");

  const shouldHighlight = typeof options.highlight === "boolean" ? options.highlight : true;

  const polygonFillAlpha = typeof options.polygonFillAlpha === "number" ? options.polygonFillAlpha : 0.35;
  const overlayWidth = typeof options.overlayWidth === "number" ? options.overlayWidth : 4;
  const overlayPointSize = typeof options.overlayPointSize === "number" ? options.overlayPointSize : 12;
  // extra size for purple points (configurable)
  const purplePointExtra = typeof options.purplePointExtra === "number" ? options.purplePointExtra : 6;
  const purplePointSize = overlayPointSize + purplePointExtra;
  const clearOverlaysFirst = options.clearPreviousOverlays !== false; // default true

  // Overlay data source management
  function getOverlayDataSource(createIfMissing = true) {
    const name = "__HIB_OVERLAYS__";
    const list = viewer.dataSources._dataSources || [];
    let found = list.find((ds) => ds.name === name || ds._name === name);
    if (found) return found;
    if (!createIfMissing) return null;
    if (typeof CustomDataSource === "undefined") throw new Error("CustomDataSource is not available.");
    found = new CustomDataSource(name);
    viewer.dataSources.add(found);
    return found;
  }

  if (clearOverlaysFirst && shouldHighlight) {
    const name = "__HIB_OVERLAYS__";
    const list = viewer.dataSources._dataSources || [];
    const idx = list.findIndex((ds) => ds.name === name || ds._name === name);
    if (idx >= 0) viewer.dataSources.remove(list[idx], true);
  }

  const overlayDS = shouldHighlight ? getOverlayDataSource() : null;

  // Normalize bounds (same logic as original)
  function normalizeBounds(b) {
    if (Array.isArray(b)) {
      const cleaned = [];
      for (let i = 0; i < b.length; i++) {
        const it = b[i] || {};
        const lat = Number(it.lat ?? it.latitude);
        const lon = Number(it.lon ?? it.lng ?? it.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          const prev = cleaned[cleaned.length - 1];
          if (!prev || Math.abs(prev.lat - lat) > 1e-12 || Math.abs(prev.lon - lon) > 1e-12) {
            cleaned.push({ lat, lon });
          }
        }
      }
      if (cleaned.length >= 4 && Math.abs(cleaned[0].lat - cleaned[cleaned.length - 1].lat) < 1e-12 && Math.abs(cleaned[0].lon - cleaned[cleaned.length - 1].lon) < 1e-12) {
        cleaned.pop();
      }
      if (cleaned.length >= 3) return { kind: "poly", poly: cleaned };
      if (cleaned.length >= 1) {
        const lats = cleaned.map((p) => p.lat);
        const lons = cleaned.map((p) => p.lon);
        return {
          kind: "rect",
          rect: { west: Math.min(...lons), south: Math.min(...lats), east: Math.max(...lons), north: Math.max(...lats) },
        };
      }
      return { kind: "rect", rect: { west: 0, south: 0, east: 0, north: 0 } };
    }
    const rect = { west: Number(b.west), south: Number(b.south), east: Number(b.east), north: Number(b.north) };
    return { kind: "rect", rect };
  }

  const NB = normalizeBounds(rawBounds);
  const boundsKind = NB.kind;
  const rectUsableForClipping = NB.kind === "rect" && NB.rect.west <= NB.rect.east;

  // Geometry helpers
  function toLLH(cart) {
    const carto = Cartographic.fromCartesian(cart);
    return { lat: CesiumMath.toDegrees(carto.latitude), lon: CesiumMath.toDegrees(carto.longitude), height: carto.height };
  }

  function isInside(lat, lon) {
    if (NB.kind === "poly") {
      const poly = NB.poly;
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].lon,
          yi = poly[i].lat;
        const xj = poly[j].lon,
          yj = poly[j].lat;
        const intersect = yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi + 1e-9) + xi;
        if (intersect) inside = !inside;
      }
      return inside;
    } else {
      const { west, south, east, north } = NB.rect;
      const withinLat = lat >= south && lat <= north;
      if (west <= east) return withinLat && lon >= west && lon <= east;
      return withinLat && (lon >= west || lon <= east);
    }
  }

  function clipSegmentToRect(p0, p1, rect) {
    if (!rectUsableForClipping) return null;
    const LEFT = 1,
      RIGHT = 2,
      BOTTOM = 4,
      TOP = 8;
    function outCode(p) {
      let code = 0;
      if (p.lon < rect.west) code |= LEFT;
      else if (p.lon > rect.east) code |= RIGHT;
      if (p.lat < rect.south) code |= BOTTOM;
      else if (p.lat > rect.north) code |= TOP;
      return code;
    }
    let a = { ...p0 },
      b = { ...p1 };
    let codeA = outCode(a),
      codeB = outCode(b);
    while (true) {
      if ((codeA | codeB) === 0) return [a, b];
      if (codeA & codeB) return null;
      const codeOut = codeA ? codeA : codeB;
      let x, y;
      const dx = b.lon - a.lon,
        dy = b.lat - a.lat;
      if (codeOut & TOP) {
        x = a.lon + (dx * (NB.rect.north - a.lat)) / dy;
        y = NB.rect.north;
      } else if (codeOut & BOTTOM) {
        x = a.lon + (dx * (NB.rect.south - a.lat)) / dy;
        y = NB.rect.south;
      } else if (codeOut & RIGHT) {
        y = a.lat + (dy * (NB.rect.east - a.lon)) / dx;
        x = NB.rect.east;
      } else if (codeOut & LEFT) {
        y = a.lat + (dy * (NB.rect.west - a.lon)) / dx;
        x = NB.rect.west;
      }
      const np = { lat: y, lon: x };
      if (codeOut === codeA) {
        a = np;
        codeA = outCode(a);
      } else {
        b = np;
        codeB = outCode(b);
      }
    }
  }

  function entityRef(entity, geometryType, dsName) {
    return { id: entity.id || entity._id || String(entity), dataSourceName: dsName, geometryType };
  }

  function getGeometry(entity) {
    if (entity.polyline?.positions) {
      const arr = entity.polyline.positions.getValue ? entity.polyline.positions.getValue() : entity.polyline.positions;
      if (Array.isArray(arr) && arr.length) return { type: "polyline", positions: arr };
    }
    if (entity.polygon?.hierarchy) {
      const h = entity.polygon.hierarchy.getValue ? entity.polygon.hierarchy.getValue() : entity.polygon.hierarchy;
      if (h && Array.isArray(h.positions) && h.positions.length) return { type: "polygon", positions: h.positions };
      if (Array.isArray(h) && h.length) return { type: "polygon", positions: h };
    }
    if (entity.position?.getValue) {
      const p = entity.position.getValue();
      if (p) return { type: "point", positions: [p] };
    }
    if (entity.billboard?.image) return { type: "billboard", positions: [entity.position?.getValue?.() || entity.position] };
    if (entity.label) return { type: "label", positions: [entity.position?.getValue?.() || entity.position] };
    return null;
  }

  function getLLHArray(positions) {
    return positions.map((c, idx) => {
      const llh = toLLH(c);
      llh.index = idx;
      return llh;
    });
  }

  function collectClippedParts(llhList) {
    const parts = [];
    let current = [];
    const useRectClipping = rectUsableForClipping;
    function flushPart(endIdx) {
      if (current.length > 0) {
        parts.push({ positions: current.slice(), startIndex: current[0].index, endIndex: endIdx });
        current = [];
      }
    }
    for (let i = 0; i < llhList.length; i++) {
      const curr = llhList[i],
        currIn = isInside(curr.lat, curr.lon);
      if (i === 0) {
        if (currIn) current.push(curr);
        continue;
      }
      const prev = llhList[i - 1],
        prevIn = isInside(prev.lat, prev.lon);
      if (prevIn && currIn) {
        if (current.length === 0) current.push(prev);
        current.push(curr);
      } else if (prevIn && !currIn) {
        if (useRectClipping) {
          const clipped = clipSegmentToRect(prev, curr, NB.rect);
          if (clipped) current.push({ ...clipped[1] });
        }
        flushPart(prev.index);
      } else if (!prevIn && currIn) {
        if (useRectClipping) {
          const clipped = clipSegmentToRect(prev, curr, NB.rect);
          if (clipped) current.push({ ...clipped[0] });
        }
        current.push(curr);
      } else {
        if (useRectClipping) {
          const clipped = clipSegmentToRect(prev, curr, NB.rect);
          if (clipped) parts.push({ positions: [{ ...clipped[0] }, { ...clipped[1] }], startIndex: prev.index, endIndex: curr.index });
        }
      }
    }
    if (current.length > 0) flushPart(llhList[llhList.length - 1].index);
    return parts;
  }

  // Visibility helpers
  function isShown(val, time) {
    if (val === undefined || val === null) return true;
    if (typeof val.getValue === "function") return !!val.getValue(time);
    return !!val;
  }

  function isEntityVisibleNow(entity, time) {
    if (!isShown(entity.show, time)) return false;
    const graphics = [entity.polyline, entity.polygon, entity.point, entity.billboard, entity.label, entity.corridor, entity.rectangle, entity.ellipse, entity.ellipsoid, entity.path];
    let anyGraphic = false;
    for (const g of graphics) {
      if (!g) continue;
      anyGraphic = true;
      if (isShown(g.show, time)) return true;
    }
    if (!anyGraphic && entity.position) return true;
    return false;
  }

  // Accumulators & temp collections (deferred visual application)
  const results = {};
  let entitiesChecked = 0;
  let entitiesMatched = 0;
  let pointsMatched = 0;

  const wholeEntities = []; // { entity, dsName, geometryType }
  const pointSelections = []; // { entity, dsName, geometryType, point, meta } where point={lat,lon,height,index}

  // Recording functions (collect data, defer visuals)
  function recordFF(entity, dsName, geometryType) {
    if (!results.f_f) results.f_f = [];
    // push entity itself
    results.f_f.push(entity);

    entitiesMatched++;
    wholeEntities.push({ entity, dsName, geometryType, reason: "f_f" });
  }

  function recordFPWhole(entity, dsName, geometryType) {
    if (!results.f_p) results.f_p = [];
    // push entity itself
    results.f_p.push(entity);

    entitiesMatched++;
    wholeEntities.push({ entity, dsName, geometryType, reason: "f_p" });
  }

  function recordPF(entity, dsName, geometryType, points) {
    if (!results.p_f) results.p_f = [];
    const ptsForResult = points.map((p) => ({ lat: p.lat, lon: p.lon, height: p.height, index: p.index }));
    results.p_f.push({ ...entityRef(entity, geometryType, dsName), points: ptsForResult });
    entitiesMatched++;
    pointsMatched += points.length;
    // push into pointSelections for deferred overlay creation
    for (const p of points) pointSelections.push({ entity, dsName, geometryType, point: p, selection: "p_f" });
  }

  function recordPP(entity, dsName, geometryType, endpoints) {
    if (!results.p_p) results.p_p = [];
    results.p_p.push({ ...entityRef(entity, geometryType, dsName), endpoints });
    entitiesMatched++;
    if (endpoints.start) {
      pointSelections.push({ entity, dsName, geometryType, point: endpoints.start, selection: "p_p_start" });
      pointsMatched++;
    }
    if (endpoints.end) {
      pointSelections.push({ entity, dsName, geometryType, point: endpoints.end, selection: "p_p_end" });
      pointsMatched++;
    }
  }

  // Iterate data sources and entities
  const dsList = viewer.dataSources._dataSources || [];
  const now = viewer.clock?.currentTime;

  for (let d = 0; d < dsList.length; d++) {
    const ds = dsList[d];
    if (ds && ds.show === false) continue;
    const dsName = ds.name || ds._name || undefined;
    const entities = ds.entities && ds.entities.values ? ds.entities.values : [];
    for (let e = 0; e < entities.length; e++) {
      const entity = entities[e];
      if (!isEntityVisibleNow(entity, now)) continue;
      const geom = getGeometry(entity);
      if (!geom) continue;
      entitiesChecked++;
      const llh = getLLHArray(geom.positions);

      if (selectionType === "f_f") {
        const allIn = llh.every((p) => isInside(p.lat, p.lon));
        if (allIn) recordFF(entity, dsName, geom.type);
      } else if (selectionType === "f_p") {
        if (geom.type === "polyline" || geom.type === "polygon") {
          const anyInside = llh.some((p) => isInside(p.lat, p.lon));
          const parts = collectClippedParts(llh);
          if (anyInside || (parts && parts.length > 0)) {
            recordFPWhole(entity, dsName, geom.type);
          }
        }
      } else if (selectionType === "p_f") {
        const pts = llh.filter((p) => isInside(p.lat, p.lon));
        if (pts.length > 0) recordPF(entity, dsName, geom.type, pts);
      } else if (selectionType === "p_p") {
        if (geom.type === "point") {
          const p0 = llh[0];
          if (isInside(p0.lat, p0.lon)) recordPP(entity, dsName, geom.type, { start: p0 });
        } else {
          const start = llh[0];
          const end = llh[llh.length - 1];
          const endpoints = {};
          if (isInside(start.lat, start.lon)) endpoints.start = start;
          if (isInside(end.lat, end.lon)) {
            if (!endpoints.start || end.index !== endpoints.start.index) endpoints.end = end;
          }
          if (endpoints.start || endpoints.end) recordPP(entity, dsName, geom.type, endpoints);
        }
      }
    }
  }

  if (!shouldHighlight) {
    const resultKey = selectionType;
    const found = !!(results[resultKey] && results[resultKey].length > 0);
    const out = {
      found,
      selectionType,
      boundsKind,
      stats: { entitiesChecked, entitiesMatched },
      results,
    };
    if (selectionType === "p_f" || selectionType === "p_p") {
      out.stats.pointsMatched = pointsMatched;
    }
    return out;
  }

  const roundHeight2 = (v) => Math.round(v * 100) / 100;

  // Compute median height among selected points
  const finiteHeights = pointSelections
    .map((s) => s.point.height)
    .filter(Number.isFinite)
    .map(roundHeight2)
    .slice();
  finiteHeights.sort((a, b) => a - b);
  let medianHeight;
  if (finiteHeights.length === 0) {
    // no valid heights -> no purple
    medianHeight = Number.POSITIVE_INFINITY;
  } else if (finiteHeights.length % 2 === 1) {
    medianHeight = finiteHeights[(finiteHeights.length - 1) / 2];
  } else {
    const hi = finiteHeights.length / 2;
    medianHeight = (finiteHeights[hi - 1] + finiteHeights[hi]) / 2;
  }

  // Helper to ensure color is fully opaque
  function ensureOpaqueColor(c) {
    if (!c) return c;
    if (typeof c.withAlpha === "function") return c.withAlpha(1.0);
    return c;
  }

  // Helper to create point overlay with chosen color
  // English short comments as requested.
  function createPointOverlay(p, color, opts = {}) {
    if (!shouldHighlight || !overlayDS) return;

    const c = ensureOpaqueColor(color);
    overlayDS.entities.add({
      position: Cartesian3.fromDegrees(p.lon, p.lat, typeof p.height === "number" ? p.height : 0.0),
      point: {
        pixelSize: typeof opts.pixelSize === "number" ? opts.pixelSize : overlayPointSize,
        color: c,
        outlineWidth: 0,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        heightReference: typeof HeightReference !== "undefined" ? HeightReference.NONE : undefined,
        zIndex: typeof opts.zIndex === "number" ? opts.zIndex : Number.MAX_SAFE_INTEGER - 1,
      },
      properties: { hibOverlay: true, isInBounding: true },
    });
  }

  // Partition selections so yellow overlays created first then purple overlays.
  const yellowSelections = [];
  const purpleSelections = [];
  for (const sel of pointSelections) {
    const h = sel.point.height;
    const h2 = Number.isFinite(h) ? roundHeight2(h) : h;
    const usePurple = Number.isFinite(h2) && h2 > medianHeight;
    if (usePurple) purpleSelections.push(sel);
    else yellowSelections.push(sel);
  }

  // Create yellow overlays first.
  for (const sel of yellowSelections) {
    createPointOverlay(sel.point, highlightColor, { pixelSize: overlayPointSize, zIndex: Number.MAX_SAFE_INTEGER - 2 });
  }

  // Then create purple overlays after. Purple overlays are forced opaque and larger.
  for (const sel of purpleSelections) {
    createPointOverlay(sel.point, ensureOpaqueColor(purpleColor), { pixelSize: purplePointSize, zIndex: Number.MAX_SAFE_INTEGER });
  }

  // Apply whole-entity coloring now (deferred), but only modify point-like visuals inside entity to purple if > median
  function applyWholeEntityColoring(item) {
    const entity = item.entity;
    const geomType = item.geometryType;

    // choose primary material wrapper
    const CMP = typeof Cesium !== "undefined" && typeof ColorMaterialProperty !== "undefined" ? ColorMaterialProperty : typeof ColorMaterialProperty !== "undefined" ? ColorMaterialProperty : null;
    const asMaterial = (c) => (CMP ? new CMP(c) : c);

    // Apply generic highlight color to non-point graphics
    if (entity.polyline) {
      if (entity.polyline.__hib_orig_material === undefined) entity.polyline.__hib_orig_material = entity.polyline.material;
      if (entity.polyline.__hib_orig_width === undefined) entity.polyline.__hib_orig_width = entity.polyline.width;
      entity.polyline.material = asMaterial(highlightColor);
      if (entity.polyline.width == null) entity.polyline.width = 3;
    }
    if (entity.path) {
      if (entity.path.__hib_orig_material === undefined) entity.path.__hib_orig_material = entity.path.material;
      if (entity.path.__hib_orig_width === undefined) entity.path.__hib_orig_width = entity.path.width;
      entity.path.material = asMaterial(highlightColor);
      if (entity.path.width == null) entity.path.width = 3;
    }
    if (entity.polygon) {
      if (entity.polygon.__hib_orig_material === undefined) entity.polygon.__hib_orig_material = entity.polygon.material;
      if (entity.polygon.__hib_orig_outline === undefined) entity.polygon.__hib_orig_outline = entity.polygon.outline;
      if (entity.polygon.__hib_orig_outlineColor === undefined) entity.polygon.__hib_orig_outlineColor = entity.polygon.outlineColor;
      entity.polygon.material = asMaterial(highlightColor.withAlpha(typeof polygonFillAlpha === "number" ? polygonFillAlpha : 0.35));
      entity.polygon.outline = true;
      entity.polygon.outlineColor = highlightColor;
    }
    if (entity.corridor) {
      if (entity.corridor.__hib_orig_material === undefined) entity.corridor.__hib_orig_material = entity.corridor.material;
      entity.corridor.material = asMaterial(highlightColor);
    }
    if (entity.rectangle) {
      if (entity.rectangle.__hib_orig_material === undefined) entity.rectangle.__hib_orig_material = entity.rectangle.material;
      if (entity.rectangle.__hib_orig_outline === undefined) entity.rectangle.__hib_orig_outline = entity.rectangle.outline;
      if (entity.rectangle.__hib_orig_outlineColor === undefined) entity.rectangle.__hib_orig_outlineColor = entity.rectangle.outlineColor;
      entity.rectangle.material = asMaterial(highlightColor.withAlpha(typeof polygonFillAlpha === "number" ? polygonFillAlpha : 0.35));
      entity.rectangle.outline = true;
      entity.rectangle.outlineColor = highlightColor;
    }
    if (entity.ellipse) {
      if (entity.ellipse.__hib_orig_material === undefined) entity.ellipse.__hib_orig_material = entity.ellipse.material;
      if (entity.ellipse.__hib_orig_outline === undefined) entity.ellipse.__hib_orig_outline = entity.ellipse.outline;
      if (entity.ellipse.__hib_orig_outlineColor === undefined) entity.ellipse.__hib_orig_outlineColor = entity.ellipse.outlineColor;
      entity.ellipse.material = asMaterial(highlightColor.withAlpha(typeof polygonFillAlpha === "number" ? polygonFillAlpha : 0.35));
      entity.ellipse.outline = true;
      entity.ellipse.outlineColor = highlightColor;
    }
    if (entity.ellipsoid) {
      if (entity.ellipsoid.__hib_orig_material === undefined) entity.ellipsoid.__hib_orig_material = entity.ellipsoid.material;
      if (entity.ellipsoid.__hib_orig_outline === undefined) entity.ellipsoid.__hib_orig_outline = entity.ellipsoid.outline;
      if (entity.ellipsoid.__hib_orig_outlineColor === undefined) entity.ellipsoid.__hib_orig_outlineColor = entity.ellipsoid.outlineColor;
      entity.ellipsoid.material = asMaterial(highlightColor.withAlpha(0.6));
      entity.ellipsoid.outline = true;
      entity.ellipsoid.outlineColor = highlightColor;
    }

    // For point-like visuals inside entity, choose purple if their height > median and make purple fully opaque and larger.
    if (entity.point) {
      if (entity.point.__hib_orig_color === undefined) entity.point.__hib_orig_color = entity.point.color;
      if (entity.point.__hib_orig_pixelSize === undefined) entity.point.__hib_orig_pixelSize = entity.point.pixelSize;
      const cart = entity.position?.getValue ? entity.position.getValue(now) : entity.position;
      let hh = NaN;
      if (cart) {
        try {
          hh = toLLH(cart).height;
        } catch (e) {
          hh = NaN;
        }
      }
      const hh2 = Number.isFinite(hh) ? roundHeight2(hh) : hh;
      const usePurple = Number.isFinite(hh2) && hh2 > medianHeight;
      entity.point.color = usePurple ? ensureOpaqueColor(purpleColor) : ensureOpaqueColor(highlightColor);
      // enlarge purple point visuals
      if (usePurple) {
        entity.point.pixelSize = Math.max(entity.point.__hib_orig_pixelSize || overlayPointSize, purplePointSize);
      } else {
        entity.point.pixelSize = entity.point.__hib_orig_pixelSize ?? overlayPointSize;
      }
      if (entity.point.disableDepthTestDistance === undefined) entity.point.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    }

    // Billboard
    if (entity.billboard) {
      if (entity.billboard.__hib_orig_color === undefined) entity.billboard.__hib_orig_color = entity.billboard.color;
      const cart = entity.position?.getValue ? entity.position.getValue(now) : entity.position;
      let hh = NaN;
      if (cart) {
        try {
          hh = toLLH(cart).height;
        } catch (e) {
          hh = NaN;
        }
      }
      const hh2 = Number.isFinite(hh) ? roundHeight2(hh) : hh;
      const usePurple = Number.isFinite(hh2) && hh2 > medianHeight;
      entity.billboard.color = usePurple ? ensureOpaqueColor(purpleColor) : ensureOpaqueColor(highlightColor);
      // no universal pixelSize for billboard, leave as-is
    }

    // Label
    if (entity.label) {
      if (entity.label.__hib_orig_fillColor === undefined) entity.label.__hib_orig_fillColor = entity.label.fillColor;
      const cart = entity.position?.getValue ? entity.position.getValue(now) : entity.position;
      let hh = NaN;
      if (cart) {
        try {
          hh = toLLH(cart).height;
        } catch (e) {
          hh = NaN;
        }
      }
      const hh2 = Number.isFinite(hh) ? roundHeight2(hh) : hh;
      const usePurple = Number.isFinite(hh2) && hh2 > medianHeight;
      entity.label.fillColor = usePurple ? ensureOpaqueColor(purpleColor) : ensureOpaqueColor(highlightColor);
      // label size control varies; not changed here
    }
  }

  // Apply whole-entity color for all collected entities
  for (const item of wholeEntities) {
    applyWholeEntityColoring(item);
  }

  // Build final result object
  const resultKey = selectionType;
  const found = !!(results[resultKey] && results[resultKey].length > 0);
  const out = {
    found,
    selectionType,
    boundsKind,
    stats: { entitiesChecked, entitiesMatched },
    results,
  };
  if (selectionType === "p_f" || selectionType === "p_p") out.stats.pointsMatched = pointsMatched;

  return out;
};

export const enablePolygonDrawing = (viewer, originalCameraControls, callback) => {
  const POLYGON_ID = "drawn-for-select";
  const POINT_PREFIX = "polygon-point-";

  let positions = [];
  let isDrawing = false;
  let pointEntities = [];

  const handler = new ScreenSpaceEventHandler(viewer.canvas);

  const addPoint = (position) => {
    const cartesian = viewer.scene.pickPosition(position);
    if (cartesian) {
      positions.push(cartesian);

      const pointEntity = viewer.entities.add({
        id: `${POINT_PREFIX}${positions.length - 1}`,
        position: cartesian,
        point: {
          pixelSize: 3,
          color: Color.BLUE,
          outlineColor: Color.BLUE,
          outlineWidth: 2,
        },
      });

      pointEntities.push(pointEntity);
    }
  };

  handler.setInputAction((click) => {
    const position = click.position;

    if (!isDrawing) {
      isDrawing = true;

      const existingPolygon = viewer.entities.getById(POLYGON_ID);
      if (existingPolygon) {
        viewer.entities.remove(existingPolygon);
      }

      pointEntities.forEach((p) => viewer.entities.remove(p));
      pointEntities = [];

      viewer.entities.add({
        id: POLYGON_ID,
        polygon: {
          hierarchy: new CallbackProperty(() => {
            if (positions.length < 2) return null;
            return new PolygonHierarchy([...positions]);
          }, false),
          material: Color.BLUE.withAlpha(0.2),
          outline: true,
          outlineColor: Color.RED,
        },
      });
    }

    addPoint(position);
  }, ScreenSpaceEventType.LEFT_CLICK);

  handler.setInputAction(() => {
    if (positions.length < 3) return;

    const cartographicPositions = positions.map((p) => Cartographic.fromCartesian(p));
    const bounds = cartographicPositions.map((c) => ({
      lat: CesiumMath.toDegrees(c.latitude),
      lon: CesiumMath.toDegrees(c.longitude),
    }));

    const polygonEntity = viewer.entities.getById(POLYGON_ID);
    if (polygonEntity) {
      polygonEntity.polygon.material = Color.BLUE.withAlpha(0.1);
      polygonEntity.polygon.outlineColor = Color.YELLOW;
    }

    callback(bounds);
    isDrawing = false;
  }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

  return handler;
};

// Add autoClear param (default false)
export const enableRectangleDrawing = (viewer, originalCameraControls, callback, autoClear = false) => {
  let startPoint; // Cartesian3 of first click
  let currentMousePosition; // Cartesian2 of the latest mouse position (window coords)
  let isDrawing = false;

  const RECTANGLE_ID = "drawn-for-select";
  const RECTANGLE2_ID = "drawnborder-for-select";

  const handler = new ScreenSpaceEventHandler(viewer.canvas);

  // Small helper to remove both entities
  const removeDrawings = () => {
    // Remove fill
    const e1 = viewer.entities.getById(RECTANGLE_ID);
    if (e1) viewer.entities.remove(e1);
    // Remove dashed border
    const e2 = viewer.entities.getById(RECTANGLE2_ID);
    if (e2) viewer.entities.remove(e2);
  };

  // --- Helper: robust window->Cartesian pick (depth first, ellipsoid fallback)
  const pickCartesian = (windowPos) => {
    // Guard: no window position -> no pick
    if (!windowPos) return undefined;

    const { scene } = viewer;
    let result;

    // Depth pick when supported (works with 3D Tiles / terrain)
    if (scene.pickPositionSupported) {
      try {
        result = scene.pickPosition(windowPos);
      } catch {
        // ignore and try fallback
      }
    }

    // Fallback to ellipsoid pick (works on the globe even without depth)
    if (!result) {
      result = scene.camera.pickEllipsoid(windowPos, scene.globe.ellipsoid);
    }

    return result || undefined;
  };

  handler.setInputAction((click) => {
    // 1) Initialize current mouse position immediately to avoid undefined on first render
    if (!click || !click.position) return;
    currentMousePosition = click.position;

    // 2) Pick start point safely
    const cartesian = pickCartesian(click.position);
    if (!cartesian) return;
    startPoint = cartesian;

    if (isDrawing) return;
    isDrawing = true;

    // Remove previous drawings if any
    removeDrawings();

    disableCameraControls(viewer, originalCameraControls);

    // --- Rectangle fill entity (dynamic via CallbackProperty)
    viewer.entities.add({
      id: RECTANGLE_ID,
      rectangle: {
        coordinates: new CallbackProperty(() => {
          // Guard: if we don't yet have positions, don't pick or compute
          if (!isDrawing || !startPoint || !currentMousePosition) return null;

          const endPoint = pickCartesian(currentMousePosition);
          if (!endPoint) return null;

          const c1 = Cartographic.fromCartesian(startPoint);
          const c2 = Cartographic.fromCartesian(endPoint);

          let west = Math.min(c1.longitude, c2.longitude);
          let south = Math.min(c1.latitude, c2.latitude);
          let east = Math.max(c1.longitude, c2.longitude);
          let north = Math.max(c1.latitude, c2.latitude);

          // --- Fix: enforce Cesium rectangle validity ---
          const EPS = 1e-12; // tiny epsilon in radians
          if (east - west < EPS) east = west + EPS; // ensure east > west
          if (north - south < EPS) north = south + EPS; // ensure north > south

          return Rectangle.fromRadians(west, south, east, north);
        }, false),
        material: Color.BLUE.withAlpha(0.08), // semi-transparent fill
        outline: true,
        outlineColor: Color.RED,
      },
    });

    // --- Outline polyline (dynamic)
    viewer.entities.add({
      id: RECTANGLE2_ID,
      polyline: {
        positions: new CallbackProperty(() => {
          // Guard: same as above
          if (!isDrawing || !startPoint || !currentMousePosition) return [];

          const endPoint = pickCartesian(currentMousePosition);
          if (!endPoint) return [];

          const c1 = Cartographic.fromCartesian(startPoint);
          const c2 = Cartographic.fromCartesian(endPoint);

          const west = Math.min(c1.longitude, c2.longitude);
          const south = Math.min(c1.latitude, c2.latitude);
          const east = Math.max(c1.longitude, c2.longitude);
          const north = Math.max(c1.latitude, c2.latitude);

          // Build closed ring
          return Cartesian3.fromRadiansArray([west, south, east, south, east, north, west, north, west, south]);
        }, false),
        width: 1,
        material: new PolylineDashMaterialProperty({
          color: Color.RED,
          gapColor: Color.TRANSPARENT,
          dashLength: 12,
        }),
        clampToGround: false,
      },
    });
  }, ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement) => {
    // Update current mouse position continuously
    if (!movement) return;
    currentMousePosition = movement.endPosition;
  }, ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    // Finalize drawing
    if (!isDrawing || !startPoint || !currentMousePosition) {
      // Safety cleanup if something went wrong mid-draw
      restoreCameraControls(viewer, originalCameraControls);
      isDrawing = false;
      return;
    }

    const endPoint = pickCartesian(currentMousePosition);
    if (!endPoint) {
      restoreCameraControls(viewer, originalCameraControls);
      isDrawing = false;
      return;
    }

    const c1 = Cartographic.fromCartesian(startPoint);
    const c2 = Cartographic.fromCartesian(endPoint);

    const bounds = {
      west: CesiumMath.toDegrees(Math.min(c1.longitude, c2.longitude)),
      south: CesiumMath.toDegrees(Math.min(c1.latitude, c2.latitude)),
      east: CesiumMath.toDegrees(Math.max(c1.longitude, c2.longitude)),
      north: CesiumMath.toDegrees(Math.max(c1.latitude, c2.latitude)),
    };

    // Freeze rectangle entity
    const rect = viewer.entities.getById(RECTANGLE_ID);
    if (rect) {
      let { west, south, east, north } = bounds;

      // --- Fix: enforce Cesium rectangle validity ---
      const EPS = 1e-8; // tiny epsilon in degrees
      if (east < west) [west, east] = [east, west]; // swap if reversed
      if (north < south) [south, north] = [north, south]; // swap if reversed
      if (east - west < EPS) east = west + EPS; // ensure east > west
      if (north - south < EPS) north = south + EPS; // ensure north > south

      rect.rectangle.coordinates = Rectangle.fromDegrees(west, south, east, north);
      rect.rectangle.material = Color.TRANSPARENT;
      rect.rectangle.outline = true;
      rect.rectangle.outlineColor = Color.YELLOW;
    }

    // Freeze outline polyline
    const border = viewer.entities.getById(RECTANGLE2_ID);
    if (border) {
      border.polyline.positions = Cartesian3.fromDegreesArray([bounds.west, bounds.south, bounds.east, bounds.south, bounds.east, bounds.north, bounds.west, bounds.north, bounds.west, bounds.south]);
      border.polyline.width = 1;
      border.polyline.material = new PolylineDashMaterialProperty({
        color: Color.RED,
        gapColor: Color.TRANSPARENT,
        dashLength: 12,
      });
      border.polyline.clampToGround = false;
    }

    // Callback first
    callback(bounds);

    // Restore controls
    restoreCameraControls(viewer, originalCameraControls);
    isDrawing = false;

    // If requested, remove both rectangle and border after finishing
    if (autoClear) {
      removeDrawings(); // delete entities
      startPoint = undefined; // reset state
      currentMousePosition = undefined;
    }
  }, ScreenSpaceEventType.LEFT_UP);

  return handler;
};

export const enableRectangleDrawingWithControlKey = (viewer, originalCameraControls, callback, autoClear = false) => {
  let startPoint;
  let currentMousePosition;
  let isDrawing = false;
  let isCtrlPressed = false;

  const RECTANGLE_ID = "drawn-for-select";
  const RECTANGLE2_ID = "drawnborder-for-select";

  const handler = new ScreenSpaceEventHandler(viewer.canvas);

  const removeDrawings = () => {
    const e1 = viewer.entities.getById(RECTANGLE_ID);
    if (e1) viewer.entities.remove(e1);

    const e2 = viewer.entities.getById(RECTANGLE2_ID);
    if (e2) viewer.entities.remove(e2);
  };

  const cancelDrawing = () => {
    removeDrawings();
    restoreCameraControls(viewer, originalCameraControls);
    startPoint = undefined;
    currentMousePosition = undefined;
    isDrawing = false;
  };

  const onKeyDown = (e) => {
    if (e.key === "Control") isCtrlPressed = true;
  };

  const onKeyUp = (e) => {
    if (e.key !== "Control") return;
    isCtrlPressed = false;
    if (isDrawing) cancelDrawing();
  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);

  const pickCartesian = (windowPos) => {
    if (!windowPos) return undefined;

    const { scene } = viewer;
    let result;

    if (scene.pickPositionSupported) {
      try {
        result = scene.pickPosition(windowPos);
      } catch {}
    }

    if (!result) {
      result = scene.camera.pickEllipsoid(windowPos, scene.globe.ellipsoid);
    }

    return result || undefined;
  };

  handler.setInputAction(
    (click) => {
      if (!click || !click.position || !isCtrlPressed) return;
      currentMousePosition = click.position;

      const cartesian = pickCartesian(click.position);
      if (!cartesian) return;
      startPoint = cartesian;

      if (isDrawing) return;
      isDrawing = true;

      removeDrawings();
      disableCameraControls(viewer, originalCameraControls);

      viewer.entities.add({
        id: RECTANGLE_ID,
        rectangle: {
          coordinates: new CallbackProperty(() => {
            if (!isDrawing || !startPoint || !currentMousePosition || !isCtrlPressed) return null;

            const endPoint = pickCartesian(currentMousePosition);
            if (!endPoint) return null;

            const c1 = Cartographic.fromCartesian(startPoint);
            const c2 = Cartographic.fromCartesian(endPoint);

            let west = Math.min(c1.longitude, c2.longitude);
            let south = Math.min(c1.latitude, c2.latitude);
            let east = Math.max(c1.longitude, c2.longitude);
            let north = Math.max(c1.latitude, c2.latitude);

            const EPS = 1e-12;
            if (east - west < EPS) east = west + EPS;
            if (north - south < EPS) north = south + EPS;

            return Rectangle.fromRadians(west, south, east, north);
          }, false),
          material: Color.BLUE.withAlpha(0.08),
          outline: true,
          outlineColor: Color.RED,
        },
      });

      viewer.entities.add({
        id: RECTANGLE2_ID,
        polyline: {
          positions: new CallbackProperty(() => {
            if (!isDrawing || !startPoint || !currentMousePosition || !isCtrlPressed) return [];

            const endPoint = pickCartesian(currentMousePosition);
            if (!endPoint) return [];

            const c1 = Cartographic.fromCartesian(startPoint);
            const c2 = Cartographic.fromCartesian(endPoint);

            const west = Math.min(c1.longitude, c2.longitude);
            const south = Math.min(c1.latitude, c2.latitude);
            const east = Math.max(c1.longitude, c2.longitude);
            const north = Math.max(c1.latitude, c2.latitude);

            return Cartesian3.fromRadiansArray([west, south, east, south, east, north, west, north, west, south]);
          }, false),
          width: 1,
          material: new PolylineDashMaterialProperty({
            color: Color.RED,
            gapColor: Color.TRANSPARENT,
            dashLength: 12,
          }),
          clampToGround: false,
        },
      });
    },
    ScreenSpaceEventType.LEFT_DOWN,
    KeyboardEventModifier.CTRL,
  );

  handler.setInputAction(
    (movement) => {
      if (!movement || !isCtrlPressed) return;
      currentMousePosition = movement.endPosition;
    },
    ScreenSpaceEventType.MOUSE_MOVE,
    KeyboardEventModifier.CTRL,
  );

  handler.setInputAction(
    () => {
      if (!isDrawing || !startPoint || !currentMousePosition || !isCtrlPressed) {
        cancelDrawing();
        return;
      }

      const endPoint = pickCartesian(currentMousePosition);
      if (!endPoint) {
        cancelDrawing();
        return;
      }

      const c1 = Cartographic.fromCartesian(startPoint);
      const c2 = Cartographic.fromCartesian(endPoint);

      const bounds = {
        west: CesiumMath.toDegrees(Math.min(c1.longitude, c2.longitude)),
        south: CesiumMath.toDegrees(Math.min(c1.latitude, c2.latitude)),
        east: CesiumMath.toDegrees(Math.max(c1.longitude, c2.longitude)),
        north: CesiumMath.toDegrees(Math.max(c1.latitude, c2.latitude)),
      };

      const rect = viewer.entities.getById(RECTANGLE_ID);
      if (rect) {
        let { west, south, east, north } = bounds;

        const EPS = 1e-8;
        if (east < west) [west, east] = [east, west];
        if (north < south) [south, north] = [north, south];
        if (east - west < EPS) east = west + EPS;
        if (north - south < EPS) north = south + EPS;

        rect.rectangle.coordinates = Rectangle.fromDegrees(west, south, east, north);
        rect.rectangle.material = Color.TRANSPARENT;
        rect.rectangle.outline = true;
        rect.rectangle.outlineColor = Color.YELLOW;
      }

      const border = viewer.entities.getById(RECTANGLE2_ID);
      if (border) {
        border.polyline.positions = Cartesian3.fromDegreesArray([
          bounds.west,
          bounds.south,
          bounds.east,
          bounds.south,
          bounds.east,
          bounds.north,
          bounds.west,
          bounds.north,
          bounds.west,
          bounds.south,
        ]);
        border.polyline.width = 1;
        border.polyline.material = new PolylineDashMaterialProperty({
          color: Color.RED,
          gapColor: Color.TRANSPARENT,
          dashLength: 12,
        });
        border.polyline.clampToGround = false;
      }

      callback(bounds);

      restoreCameraControls(viewer, originalCameraControls);
      isDrawing = false;

      if (autoClear) {
        removeDrawings();
      }

      startPoint = undefined;
      currentMousePosition = undefined;
    },
    ScreenSpaceEventType.LEFT_UP,
    KeyboardEventModifier.CTRL,
  );

  const originalDestroy = handler.destroy.bind(handler);
  handler.destroy = () => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    cancelDrawing();
    return originalDestroy();
  };

  return handler;
};

export const enableRectSelectAndCutByCutline = (viewer, originalCameraControls, mapObjectsRef, setMapObjects, enabled = true, userData) => {
  const RECTANGLE_ID = "rect-select-fill";
  const RECTANGLE_OUT_ID = "rect-select-outline";

  const removeRectanglesOnly = () => {
    try {
      const e1 = viewer.entities.getById(RECTANGLE_ID);
      if (e1) viewer.entities.remove(e1);
      const e2 = viewer.entities.getById(RECTANGLE_OUT_ID);
      if (e2) viewer.entities.remove(e2);
    } catch {}
  };

  if (!enabled) {
    try {
      viewer._rectCutHandler && !viewer._rectCutHandler.isDestroyed?.() && viewer._rectCutHandler.destroy();
    } catch {}
    removeRectanglesOnly();
    try {
      restoreCameraControls(viewer, originalCameraControls);
    } catch {}
    viewer._rectCutHandler = null;
    return null;
  }

  try {
    viewer._rectCutHandler && !viewer._rectCutHandler.isDestroyed?.() && viewer._rectCutHandler.destroy();
  } catch {}
  removeRectanglesOnly();

  let isDrawing = false;
  let startPoint = null;
  let currentMousePosition = null;
  let lastBoundsDeg = null;

  const handler = new ScreenSpaceEventHandler(viewer.canvas);
  viewer._rectCutHandler = handler;

  const now = () => JulianDate.now();
  const modifier = getUsernameAndUserIdFormat(userData);

  const normalizeLayerName = (s = "") =>
    String(s)
      .toLowerCase()
      .replace(/\.[^/.]+$/, "")
      .replace(/[\s_\-]/g, "");

  const getDSKeyName = (ds) => {
    let name = ds?.layerName || ds?.name || ds?._name || "";
    if (!name && mapObjectsRef?.current) {
      const hit = mapObjectsRef.current.find((o) => o?.dataSource === ds);
      if (hit?.file_name) name = hit.file_name;
      else if (hit?.layerName) name = hit.layerName;
      else if (hit?.name) name = hit.name;
    }
    return normalizeLayerName(name);
  };

  const ALLOWED_TARGET_TOKENS = ["lanemarker", "virtualline", "curbstone", "roadedge", "turningline", "guardrail", "concretebarrier", "lanelet"];
  const isAllowedTargetDS = (ds) => {
    const ln = getDSKeyName(ds); // NEW
    return ALLOWED_TARGET_TOKENS.some((t) => ln.includes(t));
  };

  const getMouseCartesian = (winPos) => {
    if (!winPos) return null;
    let c = viewer.scene.pickPosition(winPos);
    if (c) return c;
    c = viewer.camera.pickEllipsoid(winPos);
    if (c) return c;
    if (viewer.scene.globe) {
      const ray = viewer.camera.getPickRay(winPos);
      if (ray) {
        c = viewer.scene.globe.pick(ray, viewer.scene);
        if (c) return c;
      }
    }
    return null;
  };

  const to2D = (cartesian) => {
    const carto = Cartographic.fromCartesian(cartesian);
    return { x: CesiumMath.toDegrees(carto.longitude), y: CesiumMath.toDegrees(carto.latitude), h: carto.height };
  };

  // Check if a point (in degrees) lies inside a bounding box (also in degrees).
  // Adds safety normalization so Rectangle.fromDegrees never throws.
  const pointInBoundsDeg = (ptDeg, bounds) => {
    let { west, south, east, north } = bounds;

    // --- Fix: normalize rectangle coordinates ---
    const EPS = 1e-8; // tiny epsilon in degrees
    if (east < west) [west, east] = [east, west]; // swap if reversed
    if (north < south) [south, north] = [north, south]; // swap if reversed
    if (east - west < EPS) east = west + EPS; // ensure east > west
    if (north - south < EPS) north = south + EPS; // ensure north > south

    const rect = Rectangle.fromDegrees(west, south, east, north);
    const carto = Cartographic.fromDegrees(ptDeg.x, ptDeg.y);

    // Allow tiny tolerance (eps) to include edge cases
    const eps = 1e-7;
    return carto.longitude >= rect.west - eps && carto.longitude <= rect.east + eps && carto.latitude >= rect.south - eps && carto.latitude <= rect.north + eps;
  };

  let _toastShown = false;
  const showToastOnce = (msg, type = Type.NOTIFICATION.INFO) => {
    if (_toastShown) return;
    _toastShown = true;
    showToast(msg, type);
  };

  const removeRectangles = () => {
    try {
      const e1 = viewer.entities.getById(RECTANGLE_ID);
      if (e1) viewer.entities.remove(e1);
      const e2 = viewer.entities.getById(RECTANGLE_OUT_ID);
      if (e2) viewer.entities.remove(e2);
    } catch {}
  };

  const finalize = () => {
    removeRectangles();
    try {
      restoreCameraControls(viewer, originalCameraControls);
    } catch {}
    isDrawing = false;
    try {
      handler.destroy();
    } catch {}
    viewer._rectCutHandler = null;
  };

  const intersect2DWithT = (p1, p2, q1, q2) => {
    const P1 = to2D(p1),
      P2 = to2D(p2);
    const Q1 = to2D(q1),
      Q2 = to2D(q2);
    const denom = (P1.x - P2.x) * (Q1.y - Q2.y) - (P1.y - P2.y) * (Q1.x - Q2.x);
    if (!denom || !Number.isFinite(denom)) return null;

    const px = ((P1.x * P2.y - P1.y * P2.x) * (Q1.x - Q2.x) - (P1.x - P2.x) * (Q1.x * Q2.y - Q1.y * Q2.x)) / denom;
    const py = ((P1.x * P2.y - P1.y * P2.x) * (Q1.y - Q2.y) - (P1.y - P2.y) * (Q1.x * Q2.y - Q1.y * Q2.x)) / denom;

    const onSeg = (x, y, a, b) => {
      const eps = 1e-7;
      return x >= Math.min(a.x, b.x) - eps && x <= Math.max(a.x, b.x) + eps && y >= Math.min(a.y, b.y) - eps && y <= Math.max(a.y, b.y) + eps;
    };

    if (!(onSeg(px, py, P1, P2) && onSeg(px, py, Q1, Q2))) return null;

    const qdx = Q2.x - Q1.x,
      qdy = Q2.y - Q1.y;
    const t = Math.abs(qdx) >= Math.abs(qdy) ? (px - Q1.x) / (qdx || 1e-12) : (py - Q1.y) / (qdy || 1e-12);
    const tClamped = Math.max(0, Math.min(1, t));

    const h = Q1.h + tClamped * (Q2.h - Q1.h);
    const poi = Cartesian3.fromDegrees(px, py, h);
    return { point: poi, t: tClamped };
  };

  const findIntersectionsTargetVsOneCutterSeg = (cutterAB, targetPositions) => {
    const out = [];
    for (let i = 0; i < targetPositions.length - 1; i++) {
      const hit = intersect2DWithT(cutterAB[0], cutterAB[1], targetPositions[i], targetPositions[i + 1]);
      if (hit) out.push({ index: i + 1, point: hit.point, t: hit.t });
    }
    return out;
  };

  const dedupeIntersections = (ints) => {
    const eps = 1e-6;
    const key = (it) => `${it.index}:${Math.round(it.t / eps)}`;
    const map = new Map();
    for (const it of ints) {
      const k = key(it);
      if (!map.has(k)) map.set(k, it);
    }
    return Array.from(map.values()).sort((a, b) => a.index - b.index || a.t - b.t);
  };

  const splitPolylineAtIntersections = (polyline, intersections) => {
    if (!intersections?.length) return [polyline];
    const groups = new Map();
    intersections.forEach((it) => {
      if (!groups.has(it.index)) groups.set(it.index, []);
      groups.get(it.index).push(it);
    });
    const segments = [];
    let current = [];
    for (let i = 0; i < polyline.length; i++) {
      current.push(polyline[i]);
      const arr = groups.get(i + 1);
      if (arr?.length) {
        arr.sort((a, b) => a.t - b.t);
        for (const it of arr) {
          current.push(it.point);
          segments.push(current);
          current = [it.point];
        }
      }
    }
    if (current.length > 1) segments.push(current);
    return segments;
  };

  const getPolylineLength = (positions) => {
    if (!positions || positions.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < positions.length; i++) total += Cartesian3.distance(positions[i - 1], positions[i]);
    return total;
  };

  const readNumericId = (entity, key = "id") => {
    const v = entity.properties?.[key];
    const raw = typeof v?.getValue === "function" ? v.getValue(now()) : v;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  const getMaxPropertyIdInDataSource = (ds, key = "id") => {
    let maxId = -Infinity;
    (ds?.entities?.values || []).forEach((e) => {
      const n = readNumericId(e, key);
      if (Number.isFinite(n) && n > maxId) maxId = n;
    });
    return Number.isFinite(maxId) ? maxId : 0;
  };

  const entityHasPolylineInsideBounds = (entity, bounds) => {
    try {
      const positions = entity.polyline?.positions?.getValue(now());
      if (!Array.isArray(positions) || positions.length < 2) return false;

      for (const p of positions) {
        const d = to2D(p);
        if (pointInBoundsDeg(d, bounds)) return true;
      }

      const rectPts = [
        [bounds.west, bounds.south],
        [bounds.east, bounds.south],
        [bounds.east, bounds.north],
        [bounds.west, bounds.north],
        [bounds.west, bounds.south],
      ].map(([lon, lat]) => Cartesian3.fromDegrees(lon, lat));

      for (let i = 0; i < positions.length - 1; i++) {
        const a = positions[i],
          b = positions[i + 1];
        for (let j = 0; j < 4; j++) {
          const r1 = rectPts[j],
            r2 = rectPts[j + 1];
          const hit = intersect2DWithT(a, b, r1, r2);
          if (hit) return true;
        }
      }
    } catch {}
    return false;
  };

  const markEditedIfNeeded = (ent) => {
    if (!mapObjectsRef?.current || !setMapObjects) return;
    const temp = [...mapObjectsRef.current];
    for (const obj of temp) {
      if (obj.dataSource?.entities?.contains(ent)) {
        obj.is_edited = true;
        break;
      }
    }
    setMapObjects(temp);
  };

  // —— interactions ——
  handler.setInputAction((click) => {
    const cartesian = getMouseCartesian(click.position);
    if (!cartesian || isDrawing) return;

    startPoint = cartesian;
    isDrawing = true;
    lastBoundsDeg = null; // NEW

    removeRectangles();
    disableCameraControls(viewer, originalCameraControls);

    viewer.entities.add({
      id: RECTANGLE_ID,
      rectangle: {
        coordinates: new CallbackProperty(() => {
          const endPoint = getMouseCartesian(currentMousePosition);
          if (!endPoint) return null;
          const c1 = Cartographic.fromCartesian(startPoint);
          const c2 = Cartographic.fromCartesian(endPoint);
          const west = Math.min(c1.longitude, c2.longitude);
          const south = Math.min(c1.latitude, c2.latitude);
          const east = Math.max(c1.longitude, c2.longitude);
          const north = Math.max(c1.latitude, c2.latitude);
          return Rectangle.fromRadians(west, south, east, north);
        }, false),
        material: Color.BLUE.withAlpha(0.08),
        outline: true,
        outlineColor: Color.RED,
      },
    });

    viewer.entities.add({
      id: RECTANGLE_OUT_ID,
      polyline: {
        positions: new CallbackProperty(() => {
          const endPoint = getMouseCartesian(currentMousePosition);
          if (!endPoint) return [];
          const c1 = Cartographic.fromCartesian(startPoint);
          const c2 = Cartographic.fromCartesian(endPoint);
          const west = Math.min(c1.longitude, c2.longitude);
          const south = Math.min(c1.latitude, c2.latitude);
          const east = Math.max(c1.longitude, c2.longitude);
          const north = Math.max(c1.latitude, c2.latitude);
          return Cartesian3.fromRadiansArray([west, south, east, south, east, north, west, north, west, south]);
        }, false),
        width: 1,
        material: new PolylineDashMaterialProperty({
          color: Color.RED,
          gapColor: Color.TRANSPARENT,
          dashLength: 12,
        }),
        clampToGround: false,
      },
    });
  }, ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement) => {
    currentMousePosition = movement.endPosition;

    if (isDrawing && startPoint && currentMousePosition) {
      const endPoint = getMouseCartesian(currentMousePosition);
      if (endPoint) {
        const c1 = Cartographic.fromCartesian(startPoint);
        const c2 = Cartographic.fromCartesian(endPoint);
        lastBoundsDeg = {
          west: CesiumMath.toDegrees(Math.min(c1.longitude, c2.longitude)),
          south: CesiumMath.toDegrees(Math.min(c1.latitude, c2.latitude)),
          east: CesiumMath.toDegrees(Math.max(c1.longitude, c2.longitude)),
          north: CesiumMath.toDegrees(Math.max(c1.latitude, c2.latitude)),
        };
      }
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    let bounds = lastBoundsDeg;

    if (!bounds) {
      if (!startPoint || !currentMousePosition) {
        finalize();
        return;
      }
      const endPoint = getMouseCartesian(currentMousePosition);
      if (!endPoint) {
        finalize();
        return;
      }
      const c1 = Cartographic.fromCartesian(startPoint);
      const c2 = Cartographic.fromCartesian(endPoint);
      bounds = {
        west: CesiumMath.toDegrees(Math.min(c1.longitude, c2.longitude)),
        south: CesiumMath.toDegrees(Math.min(c1.latitude, c2.latitude)),
        east: CesiumMath.toDegrees(Math.max(c1.longitude, c2.longitude)),
        north: CesiumMath.toDegrees(Math.max(c1.latitude, c2.latitude)),
      };
    }

    // Freeze
    const rect = viewer.entities.getById(RECTANGLE_ID);
    if (rect) {
      let { west, south, east, north } = bounds;

      // --- Fix: normalize rectangle coordinates ---
      const EPS = 1e-8; // tiny epsilon in degrees
      if (east < west) [west, east] = [east, west]; // swap if reversed
      if (north < south) [south, north] = [north, south]; // swap if reversed
      if (east - west < EPS) east = west + EPS; // ensure east > west
      if (north - south < EPS) north = south + EPS; // ensure north > south

      rect.rectangle.coordinates = Rectangle.fromDegrees(west, south, east, north);
      rect.rectangle.material = Color.TRANSPARENT;
      rect.rectangle.outline = true;
      rect.rectangle.outlineColor = Color.YELLOW;
    }

    const out = viewer.entities.getById(RECTANGLE_OUT_ID);
    if (out) {
      out.polyline.positions = Cartesian3.fromDegreesArray([bounds.west, bounds.south, bounds.east, bounds.south, bounds.east, bounds.north, bounds.west, bounds.north, bounds.west, bounds.south]);
    }

    // collect
    const allDS = viewer.dataSources?._dataSources || [];
    const selected = [];
    for (const ds of allDS) {
      const ents = ds.entities?.values || [];
      for (const e of ents) {
        if (!e.polyline) continue;
        if (entityHasPolylineInsideBounds(e, bounds)) selected.push({ ds, entity: e });
      }
    }

    if (selected.length === 0) {
      showToastOnce("No line entities found in the selected area.", Type.NOTIFICATION.INFO);
      finalize();
      return;
    }

    const cutlineCandidates = new Set();
    for (const { ds } of selected) {
      const ln = getDSKeyName(ds); // NEW
      if (ln.includes("cutline")) cutlineCandidates.add(ds);
    }

    if (cutlineCandidates.size === 0) {
      showToastOnce("No 'cutline' layer found inside the selection.", Type.NOTIFICATION.WARNING);
      finalize();
      return;
    }

    const timeNow = now();
    const getPositions = (ent) => {
      try {
        const p = ent.polyline?.positions?.getValue(timeNow);
        return Array.isArray(p) ? p : null;
      } catch {
        return null;
      }
    };

    const cutterEntities = [];
    for (const ds of cutlineCandidates) {
      const ents = ds.entities?.values || [];
      for (const e of ents) {
        if (!e.polyline) continue;
        if (entityHasPolylineInsideBounds(e, bounds)) cutterEntities.push({ ds, entity: e });
      }
    }
    if (cutterEntities.length === 0) {
      showToastOnce("No cutline polyline found inside the selection.", Type.NOTIFICATION.WARNING);
      finalize();
      return;
    }

    const cutterSegments = [];
    for (const { entity: ce } of cutterEntities) {
      const pos = getPositions(ce);
      if (!pos || pos.length < 2) continue;
      for (let i = 0; i < pos.length - 1; i++) cutterSegments.push([pos[i], pos[i + 1]]);
    }

    // allocators (allowed, non-cutline)
    const dsNextId = new Map();
    for (const { ds } of selected) {
      if (cutlineCandidates.has(ds)) continue;
      if (!isAllowedTargetDS(ds)) continue;
      if (!dsNextId.has(ds)) dsNextId.set(ds, getMaxPropertyIdInDataSource(ds, "id"));
    }
    const nextIdForDS = (ds, minimum = 0) => {
      const cur = dsNextId.get(ds) ?? 0;
      const base = Math.max(cur, minimum);
      const next = base + 2;
      dsNextId.set(ds, next);
      return next;
    };

    let didAnySplit = false;

    for (const { ds, entity } of selected) {
      if (cutlineCandidates.has(ds)) continue;
      if (!isAllowedTargetDS(ds)) continue;

      const targetPos = getPositions(entity);
      if (!targetPos || targetPos.length < 2) continue;

      let allInts = [];
      for (const seg of cutterSegments) {
        const ints = findIntersectionsTargetVsOneCutterSeg(seg, targetPos);
        if (ints?.length) allInts = allInts.concat(ints);
      }
      allInts = dedupeIntersections(allInts);
      if (!allInts.length) continue;

      const segments = splitPolylineAtIntersections(targetPos, allInts);
      if (!segments || segments.length < 2) continue;

      let longestIdx = 0,
        bestLen = -1;
      for (let i = 0; i < segments.length; i++) {
        const L = getPolylineLength(segments[i]);
        if (L > bestLen) {
          bestLen = L;
          longestIdx = i;
        }
      }

      const propsIn = entity.properties;
      const propsRaw = propsIn?.getValue ? propsIn.getValue(timeNow) : propsIn;
      const originalPropsObj = propsRaw && typeof propsRaw === "object" ? { ...propsRaw } : {};
      const originalId = readNumericId(entity, "id");
      if (Number.isFinite(originalId)) {
        dsNextId.set(ds, Math.max(dsNextId.get(ds) ?? 0, originalId));
      }

      markEditedIfNeeded(entity);
      ds.entities.remove(entity);

      for (let idx = 0; idx < segments.length; idx++) {
        const seg = segments[idx];
        const isLargest = idx === longestIdx;

        const newProps = { ...originalPropsObj };
        if (modifier) newProps.modifier = modifier;
        if (Number.isFinite(originalId)) {
          newProps.id = isLargest ? originalId : nextIdForDS(ds, originalId);
        }

        const newEntity = new Entity({
          polyline: {
            positions: seg,
            width: entity.polyline.width,
            material: entity.polyline.material,
            clampToGround: entity.polyline.clampToGround,
            heightReference: entity.polyline.heightReference,
          },
          properties: new PropertyBag(newProps),
          geojsonType: entity.geojsonType,
          id: `${entity.id}_rectcut_${idx}_${typeof createUniqueId === "function" ? createUniqueId() : Date.now()}`,
        });

        ds.entities.add(newEntity);
      }

      didAnySplit = true;
    }

    if (didAnySplit) showToastOnce("Selected lines were sliced by cutline.", Type.NOTIFICATION.SUCCESS);
    else showToastOnce("No eligible lines to slice in the selection.", Type.NOTIFICATION.INFO);

    finalize();
  }, ScreenSpaceEventType.LEFT_UP);

  return handler;
};

export const registerIsInBoundingClick = (viewer, callback) => {
  const handler = new ScreenSpaceEventHandler(viewer.canvas);

  // Helper: read a property that might be a Cesium Property or a plain value
  const readProp = (props, key) => {
    if (!props) return undefined;
    const v = props[key];
    if (v && typeof v.getValue === "function") {
      return v.getValue(JulianDate.now());
    }
    return v;
  };

  handler.setInputAction((movement) => {
    if (!movement || !movement.position) return;

    const picked = viewer.scene.drillPick(movement.position) || [];
    for (const hit of picked) {
      const entity = hit && hit.id instanceof Entity ? hit.id : null;
      if (!entity) continue;

      const isInBounding = readProp(entity.properties, "isInBounding");
      if (isInBounding !== true) continue;

      let cartesian;

      if (entity.position && typeof entity.position.getValue === "function") {
        cartesian = entity.position.getValue(JulianDate.now());
      }

      if (!defined(cartesian) && viewer.scene.pickPositionSupported) {
        try {
          const p = viewer.scene.pickPosition(movement.position);
          if (defined(p)) cartesian = p;
        } catch {
          // keep behavior: just treat as no position
        }
      }

      if (!defined(cartesian)) continue;

      let carto;
      try {
        carto = Cartographic.fromCartesian(cartesian);
      } catch {
        continue; // prevent "cartesian is required" crash
      }

      const lat = CesiumMath.toDegrees(carto.latitude);
      const lon = CesiumMath.toDegrees(carto.longitude);
      const height = carto.height;

      callback({ lat, lon, height, entity, id: entity.id });
      return;
    }
  }, ScreenSpaceEventType.LEFT_CLICK);

  return handler;
};

export const restoreEntityColors = (viewer) => {
  if (!viewer || !viewer.dataSources) return;

  viewer.dataSources._dataSources.forEach((ds) => {
    ds.entities.values.forEach((entity) => {
      if (entity.polyline && entity.polyline._originalMaterial) {
        entity.polyline.material = entity.polyline._originalMaterial;
        delete entity.polyline._originalMaterial;
      }
    });
  });
};
